import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { analyticsReporterApi } from '@/lib/api/analytics-reporter.api';

import type { AnalystReportQuery, CreateAnalystReportDto } from '@repo/shared-types';

export function useAnalystPresets() {
  return useQuery({
    queryKey: ['analyst-presets'],
    queryFn: () => analyticsReporterApi.getPresets(),
    staleTime: Infinity,
  });
}

export function useAnalystReports(query: Partial<AnalystReportQuery> = {}) {
  return useQuery({
    queryKey: ['analyst-reports', query],
    queryFn: () => analyticsReporterApi.getReports(query),
  });
}

export function useAnalystReport(id: string | undefined) {
  return useQuery({
    queryKey: ['analyst-reports', id],
    queryFn: () => analyticsReporterApi.getReport(id!),
    enabled: !!id,
    // Poll while the report is still being generated.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'GENERATING' ? 4000 : false;
    },
  });
}

export function useCreateAnalystReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAnalystReportDto) => analyticsReporterApi.createReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analyst-reports'] });
    },
  });
}

export function useDeleteAnalystReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsReporterApi.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analyst-reports'] });
    },
  });
}
